/**
 * Closer EOD Form - Public submission form
 * URL: /closer-eod
 */

import { useState } from "react";
import {
  EODFormLayout,
  FormField,
  TextInput,
  TextArea,
  Select,
  RatingInput,
  SubmitButton,
  SuccessMessage,
  ErrorMessage,
} from "./EODFormLayout";

const CLOSERS = [
  { value: "Brad", label: "Brad" },
  { value: "Tristen", label: "Tristen" },
  { value: "Alejandro", label: "Alejandro" },
  { value: "Callum Innes", label: "Callum Innes" },
  { value: "Daniel Garces", label: "Daniel Garces" },
  { value: "Jaxon Shorter", label: "Jaxon Shorter" },
  { value: "Soumil Kansal", label: "Soumil Kansal" },
  { value: "Seif Farghali", label: "Seif Farghali" },
  { value: "Christian Ramos", label: "Christian Ramos" },
  { value: "Jack Andrews", label: "Jack Andrews" },
  { value: "Richard L", label: "Richard L" },
];

const CASH_BLOCK_OPTIONS = [
  { value: "Yes - all showed", label: "Yes all my calls showed and my cash block was filled" },
  { value: "Yes - used no show time", label: "Yes I used the time when I had no shows to do cash producing activities" },
  { value: "No", label: "No I didn't use my cash block time for cash producing activities" },
];

const YES_NO_OPTIONS = [
  { value: "Yes", label: "Yes" },
  { value: "No", label: "No" },
];

const CALENDAR_ADHERENCE_OPTIONS = [
  { value: "1-3", label: "1-3 - Poorly" },
  { value: "4-6", label: "4-6 - Okay" },
  { value: "7-8", label: "7-8 - Good" },
  { value: "9-10", label: "9-10 - Excellent" },
];

interface FormData {
  name: string;
  eodDate: string;
  callsOnCalendar: string;
  showedUp: string;
  offersMade: string;
  noShows: string;
  reschedules: string;
  closes: string;
  cashGenerated: string;
  wentWell: string;
  couldImprove: string;
  leadflowConfidence: number | undefined;
  cashBlockEffective: string;
  mrrsUpToDate: string;
  calendarAdherence: string;
  needHelp: string;
}

function getTodayDate(): string {
  return new Date().toISOString().split("T")[0] ?? "";
}

const initialFormData: FormData = {
  name: "",
  eodDate: getTodayDate(),
  callsOnCalendar: "",
  showedUp: "",
  offersMade: "",
  noShows: "",
  reschedules: "",
  closes: "",
  cashGenerated: "",
  wentWell: "",
  couldImprove: "",
  leadflowConfidence: undefined,
  cashBlockEffective: "",
  mrrsUpToDate: "",
  calendarAdherence: "",
  needHelp: "",
};

export function CloserEodForm() {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateField = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch("/api/eod/submit/closer", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name,
          eodDate: formData.eodDate,
          callsOnCalendar: parseInt(formData.callsOnCalendar) || 0,
          showedUp: parseInt(formData.showedUp) || 0,
          offersMade: parseInt(formData.offersMade) || 0,
          noShows: parseInt(formData.noShows) || 0,
          reschedules: parseInt(formData.reschedules) || 0,
          closes: parseInt(formData.closes) || 0,
          cashGenerated: parseInt(formData.cashGenerated) || 0,
          wentWell: formData.wentWell,
          couldImprove: formData.couldImprove,
          leadflowConfidence: formData.leadflowConfidence,
          cashBlockEffective: formData.cashBlockEffective,
          mrrsUpToDate: formData.mrrsUpToDate,
          calendarAdherence: formData.calendarAdherence,
          needHelp: formData.needHelp,
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || "Failed to submit EOD");
      }

      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData(initialFormData);
    setSubmitted(false);
    setError(null);
  };

  if (submitted) {
    return (
      <EODFormLayout title="Closer EOD Report" subtitle="Daily report for closers">
        <SuccessMessage onReset={handleReset} />
      </EODFormLayout>
    );
  }

  return (
    <EODFormLayout title="Closer EOD Report" subtitle="Fill out your daily closing report">
      {error && <ErrorMessage message={error} onRetry={() => setError(null)} />}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField label="Who is filling this form out?" required>
            <Select
              value={formData.name}
              onChange={(v) => updateField("name", v)}
              options={CLOSERS}
              placeholder="Select your name"
              required
            />
          </FormField>

          <FormField label="What date is this EOD for?" required>
            <TextInput
              type="date"
              value={formData.eodDate}
              onChange={(v) => updateField("eodDate", v)}
              required
            />
          </FormField>
        </div>

        {/* Call Metrics */}
        <div className="border-t border-white/10 pt-6">
          <h3 className="text-lg font-semibold text-white mb-4">Call Metrics</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <FormField label="How many calls did you have on calendar today?" required hint="Just put number">
              <TextInput
                type="number"
                value={formData.callsOnCalendar}
                onChange={(v) => updateField("callsOnCalendar", v)}
                placeholder="0"
                min={0}
                required
              />
            </FormField>

            <FormField label="Out of the calls booked - how many showed up?" required hint="Just put number">
              <TextInput
                type="number"
                value={formData.showedUp}
                onChange={(v) => updateField("showedUp", v)}
                placeholder="0"
                min={0}
                required
              />
            </FormField>

            <FormField label="Out of these - how many offers did you make?" required hint="Just put number">
              <TextInput
                type="number"
                value={formData.offersMade}
                onChange={(v) => updateField("offersMade", v)}
                placeholder="0"
                min={0}
                required
              />
            </FormField>

            <FormField label="How many no shows did you have today?" required hint="Just put number">
              <TextInput
                type="number"
                value={formData.noShows}
                onChange={(v) => updateField("noShows", v)}
                placeholder="0"
                min={0}
                required
              />
            </FormField>

            <FormField label="How many reschedules did you have today?" required hint="Just put number">
              <TextInput
                type="number"
                value={formData.reschedules}
                onChange={(v) => updateField("reschedules", v)}
                placeholder="0"
                min={0}
                required
              />
            </FormField>

            <FormField label="How many closes did you have today?" required hint="Just put number">
              <TextInput
                type="number"
                value={formData.closes}
                onChange={(v) => updateField("closes", v)}
                placeholder="0"
                min={0}
                required
              />
            </FormField>
          </div>

          <div className="mt-4">
            <FormField label="How much cash did you generate for the day?" required hint="Just put number">
              <TextInput
                type="number"
                value={formData.cashGenerated}
                onChange={(v) => updateField("cashGenerated", v)}
                placeholder="0"
                min={0}
                required
              />
            </FormField>
          </div>
        </div>

        {/* Feedback Section */}
        <div className="border-t border-white/10 pt-6">
          <h3 className="text-lg font-semibold text-white mb-4">Daily Feedback</h3>
          <div className="space-y-4">
            <FormField label="What went well today?" required>
              <TextArea
                value={formData.wentWell}
                onChange={(v) => updateField("wentWell", v)}
                placeholder="Describe what worked..."
                rows={3}
                required
              />
            </FormField>

            <FormField label="What could have went better?" required>
              <TextArea
                value={formData.couldImprove}
                onChange={(v) => updateField("couldImprove", v)}
                placeholder="Areas for improvement..."
                rows={3}
                required
              />
            </FormField>
          </div>
        </div>

        {/* Leadflow Confidence */}
        <div className="border-t border-white/10 pt-6">
          <FormField
            label="What is your confidence level with leadflow quality TODAY ONLY?"
            hint="0 = horrible, 10 = solid. ONLY PUT IF YOU HAVE AN HONEST ANSWER. If no one showed to the call or you had zero calls, don't answer."
          >
            <RatingInput
              value={formData.leadflowConfidence}
              onChange={(v) => updateField("leadflowConfidence", v)}
            />
          </FormField>
        </div>

        {/* Operations Questions */}
        <div className="border-t border-white/10 pt-6">
          <h3 className="text-lg font-semibold text-white mb-4">Operations</h3>
          <div className="space-y-4">
            <FormField label="Did you use your cash block time effectively?" required hint="No show occurs, hitting up MRRs, collecting deposits, dialing no shows, etc.">
              <Select
                value={formData.cashBlockEffective}
                onChange={(v) => updateField("cashBlockEffective", v)}
                options={CASH_BLOCK_OPTIONS}
                placeholder="Select an option"
                required
              />
            </FormField>

            <FormField label="Are all of your MRRs caught up to date?" required>
              <Select
                value={formData.mrrsUpToDate}
                onChange={(v) => updateField("mrrsUpToDate", v)}
                options={YES_NO_OPTIONS}
                placeholder="Select an option"
                required
              />
            </FormField>

            <FormField label="How well did you stick to your calendar today?" required>
              <Select
                value={formData.calendarAdherence}
                onChange={(v) => updateField("calendarAdherence", v)}
                options={CALENDAR_ADHERENCE_OPTIONS}
                placeholder="Select a rating"
                required
              />
            </FormField>

            <FormField label="Do you need help with something from Jack to perform better?" required>
              <TextArea
                value={formData.needHelp}
                onChange={(v) => updateField("needHelp", v)}
                placeholder="Describe what you need help with..."
                rows={2}
                required
              />
            </FormField>
          </div>
        </div>

        {/* Submit */}
        <div className="pt-4">
          <SubmitButton loading={loading} />
        </div>
      </form>
    </EODFormLayout>
  );
}

export default CloserEodForm;
